import React from "react";
import { create } from "zustand";

import NotificationBadge from "../../../../global/NotificationBadge";
import HNSColors from "../../../../ui library/HNSColors";

const DEFAULT_TOAST_DURATION_MS = 3000;
const DEFAULT_TOAST_BACKGROUND_COLOR = HNSColors.secondary[950];
const DEFAULT_TOAST_TEXT_COLOR = HNSColors.neutrals[200];

const BASE_TOAST_STATE = {
  backgroundColor: DEFAULT_TOAST_BACKGROUND_COLOR,
  duration: DEFAULT_TOAST_DURATION_MS,
  icon: <NotificationBadge />,
  isOpen: false,
  message: "",
  navigation: null,
  textColor: DEFAULT_TOAST_TEXT_COLOR,
};

const normalizeToastConfig = (config) => ({
  backgroundColor: config.backgroundColor ?? DEFAULT_TOAST_BACKGROUND_COLOR,
  duration: config.duration ?? DEFAULT_TOAST_DURATION_MS,
  icon: config.icon ?? <NotificationBadge />, // Default icon when not provided
  message: config.message,
  navigation: config.navigation ?? null,
  textColor: config.textColor ?? DEFAULT_TOAST_TEXT_COLOR,
});

export const useToastStore = create((set, get) => ({
  ...BASE_TOAST_STATE,
  queue: [],

  enqueueToast: (config = {}) => {
    if (!config?.message) {
      return false;
    }

    set((state) => ({ queue: [...state.queue, config] }));

    if (!get().isOpen) {
      get().showNextToast();
    }

    return true;
  },

  // Utility method for easier toast triggering
  requestToast: async (config = {}) => get().enqueueToast(config),

  showNextToast: () => {
    const { queue } = get();

    if (!queue.length) {
      return false;
    }

    const [next, ...rest] = queue;

    set({
      ...BASE_TOAST_STATE,
      ...normalizeToastConfig(next),
      isOpen: true,
      queue: rest,
    });

    return true;
  },

  // Close the currently displayed toast
  closeToast: () =>
    set((state) => ({
      ...state,
      isOpen: false,
    })),

  // Prevent to clear the info while closing (for animation purposes)
  resetToast: () => {
    set((state) => ({
      ...state,
      ...BASE_TOAST_STATE,
    }));

    get().showNextToast();
  },

  // Clear the toast queue
  clearQueue: () =>
    set((state) => ({
      ...state,
      queue: [],
    })),

  // Clear queue and reset current toast (for example on logout)
  clearAllToasts: () =>
    set(() => ({
      ...BASE_TOAST_STATE,
      queue: [],
    })),
}));
